"use client";

import React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { Download, FileText, ArrowRight, BookOpen, Layers } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardHeader, CardTitle, CardDescription, CardContent, CardFooter } from "@/components/ui/card";
interface GuideItem {
  id: string;
  category: string;
  format: string;
  title: string;
  summary: string;
  pages: string;
  fileSize: string;
  coverImage: string;
  coverSrcset: string;
  coverSizes: string;
  downloadUrl: string;
}
const guidesList: GuideItem[] = [{
  id: "guide-1",
  category: "Corporate Strategy",
  format: "Strategic Guide",
  title: "The B2B Executive Playbook: Aligning Revenue Engines in Volatile Markets",
  summary: "A practical framework for restructuring pipeline targets, pricing power, and cross-functional teams.",
  pages: "48 pages",
  fileSize: "4.2 MB PDF",
  coverImage: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1789806/img/b2b_executive_playbook_cover.png",
  coverSrcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1789806/img/b2b_executive_playbook_cover.png-wvc-srcset",
  coverSizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1789806/img/b2b_executive_playbook_cover.png-wvc-sizes",
  downloadUrl: "/ResourcesPage"
}, {
  id: "guide-2",
  category: "Operational Governance",
  format: "Executive Framework",
  title: "Data Sovereignty and Compliance: Navigating Global Regulatory Standards",
  summary: "A systematic audit methodology to safeguard enterprise communications and maintain strict data privacy.",
  pages: "36 pages",
  fileSize: "3.1 MB PDF",
  coverImage: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1789806/img/data_sovereignty_guide_cover.png",
  coverSrcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1789806/img/data_sovereignty_guide_cover.png-wvc-srcset",
  coverSizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1789806/img/data_sovereignty_guide_cover.png-wvc-sizes",
  downloadUrl: "/PrivacyDataProtectionPage"
}, {
  id: "guide-3",
  category: "Market Analysis",
  format: "Annual Ebook",
  title: "Forecasting Capital Allocations in B2B Media & Tech Publishing",
  summary: "Empirical evaluation of audience retention, subscription modeling, and editorial investment benchmarks.",
  pages: "64 pages",
  fileSize: "5.8 MB PDF",
  coverImage: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1789806/img/capital_allocations_ebook_cover.png",
  coverSrcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1789806/img/capital_allocations_ebook_cover.png-wvc-srcset",
  coverSizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1789806/img/capital_allocations_ebook_cover.png-wvc-sizes",
  downloadUrl: "/ResearchReportsPage"
}, {
  id: "guide-4",
  category: "RevOps & Sales",
  format: "Tactical Handbook",
  title: "Enterprise Deal Velocity: Reducing Cycle Times Across Complex Buyer Groups",
  summary: "Clear diagnostic checklists to map buying committee priorities and identify stalled procurement phases.",
  pages: "42 pages",
  fileSize: "3.7 MB PDF",
  coverImage: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1789806/img/deal_velocity_handbook_cover.png",
  coverSrcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1789806/img/deal_velocity_handbook_cover.png-wvc-srcset",
  coverSizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1789806/img/deal_velocity_handbook_cover.png-wvc-sizes",
  downloadUrl: "/CaseStudiesPage"
}, {
  id: "guide-5",
  category: "Publishing Ethics",
  format: "Editorial Standard",
  title: "The Peer-Review Blueprint: Ensuring Editorial Independence in Corporate Media",
  summary: "Protocol documentation on separating sponsored narratives from peer-reviewed market research.",
  pages: "28 pages",
  fileSize: "2.4 MB PDF",
  coverImage: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1789806/img/peer_review_blueprint_cover.png",
  coverSrcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1789806/img/peer_review_blueprint_cover.png-wvc-srcset",
  coverSizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1789806/img/peer_review_blueprint_cover.png-wvc-sizes",
  downloadUrl: "/AboutUsPage"
}, {
  id: "guide-6",
  category: "Product & Tech",
  format: "Architecture Brief",
  title: "Applied AI in Knowledge Management: Structured Insights Without Hallucination",
  summary: "Architectural guidelines for deploying verified knowledge retrieval systems across enterprise research desks.",
  pages: "52 pages",
  fileSize: "4.9 MB PDF",
  coverImage: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1789806/img/ai_knowledge_management_cover.png",
  coverSrcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1789806/img/ai_knowledge_management_cover.png-wvc-srcset",
  coverSizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1789806/img/ai_knowledge_management_cover.png-wvc-sizes",
  downloadUrl: "/IndustryInsightsPage"
}];
export default function Guides() {
  const prefersReducedMotion = useReducedMotion();
  const containerVariants = {
    hidden: {},
    visible: {
      transition: {
        staggerChildren: prefersReducedMotion ? 0 : 0.08
      }
    }
  };
  const itemVariants = {
    hidden: prefersReducedMotion ? {
      opacity: 1,
      y: 0
    } : {
      opacity: 0,
      y: 16
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.45,
        ease: [0.16, 1, 0.3, 1]
      }
    }
  };
  return <section id="ebooks-guides-grid" data-nav="light" className="relative bg-background text-foreground py-16 sm:py-20 lg:py-24 border-b border-border">
      <div className="mx-auto max-w-[1280px] px-5 sm:px-8 lg:px-12">
        {/* Section Header with broadsheet styling */}
        <div className="mb-12 sm:mb-16">
          <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 pb-6 border-b border-border">
            <div className="max-w-2xl">
              <div className="flex items-center gap-2 mb-3">
                <span className="inline-block size-2 rounded-full bg-accent" />
                <span className="text-xs font-semibold uppercase tracking-wider text-muted-foreground font-sans">
                  Curated Resource Library
                </span>
              </div>
              <h2 className="text-2xl sm:text-3xl lg:text-4xl font-semibold font-serif tracking-tight text-foreground">
                Tactical guides for executive decision-makers
              </h2>
              <p className="mt-3 text-base sm:text-lg text-muted-foreground font-sans">
                Browse curated frameworks, analytical reports, and operational playbooks designed for senior leaders.
              </p>
            </div>

            <div className="flex items-center gap-3">
              <div className="hidden sm:flex items-center gap-2 text-xs font-mono text-muted-foreground bg-muted px-3 py-1.5 rounded-sm border border-border">
                <FileText className="size-3.5 text-accent" />
                <span>6 Publications Available</span>
              </div>
              <Button asChild variant="outline" size="sm" className="rounded-sm text-xs font-medium">
                <Link to="/NewsletterPage">
                  <span>Get direct dispatch</span>
                  <ArrowRight className="size-3.5" />
                </Link>
              </Button>
            </div>
          </div>
          {/* Masthead rule dual divider */}
          <div className="h-[0.5px] bg-border mt-1" />
        </div>

        {/* 6-Card Publication Grid */}
        <motion.div variants={containerVariants} initial="hidden" whileInView="visible" viewport={{
        once: true,
        amount: 0.1
      }} className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8">
          {guidesList.map((guide, index) => <motion.div key={guide.id} data-index={index} variants={itemVariants} className="h-full">
              <Card className="h-full flex flex-col justify-between rounded-md border border-border bg-card shadow-xs transition-[box-shadow,transform] duration-250 hover:shadow-md hover:-translate-y-1 overflow-hidden group">
                <div>
                  {/* Publication Spine Graphic Presentation */}
                  <div className="relative aspect-[16/10] bg-muted/60 border-b border-border overflow-hidden flex items-center justify-center p-6">
                    {/* Spine gradient effect on left edge */}
                    <div className="absolute inset-y-0 left-0 w-8 z-10 pointer-events-none bg-gradient-to-r from-foreground/15 via-foreground/5 to-transparent" />
                    
                    <div className="relative w-36 h-48 sm:w-40 sm:h-52 bg-primary rounded-sm shadow-md flex flex-col justify-between p-4 text-primary-foreground border border-primary/20 transition-transform duration-300 group-hover:scale-[1.02]">
                      {/* Realistic book spine crease */}
                      <div className="absolute top-0 bottom-0 left-2 w-px bg-primary-foreground/15" />
                      
                      <div className="space-y-1.5 pl-2">
                        <span className="text-[9px] font-mono uppercase tracking-widest text-accent font-semibold block">
                          {guide.format}
                        </span>
                        <p className="text-xs font-serif font-semibold leading-tight line-clamp-3 text-primary-foreground">
                          {guide.title}
                        </p>
                      </div>

                      <div className="pl-2 pt-2 border-t border-primary-foreground/10 flex items-center justify-between text-[10px] font-mono text-primary-foreground/70">
                        <span>MinfoMedia</span>
                        <span>{guide.pages}</span>
                      </div>
                    </div>

                    {/* Format pill in image header */}
                    <div className="absolute top-3 right-3 z-10">
                      <Badge variant="outline" className="bg-background/90 text-foreground border-border text-[11px] font-mono rounded-sm px-2 py-0.5 backdrop-blur-xs">
                        {guide.fileSize}
                      </Badge>
                    </div>
                  </div>

                  {/* Editorial Card Body */}
                  <CardHeader className="p-6 pb-3 space-y-2">
                    <div className="flex items-center gap-2">
                      <span className="text-xs font-semibold uppercase tracking-wider text-accent font-sans">
                        {guide.category}
                      </span>
                      <span className="text-muted-foreground text-xs">•</span>
                      <span className="text-xs font-mono text-muted-foreground">
                        {guide.pages}
                      </span>
                    </div>

                    <CardTitle className="text-lg sm:text-xl font-serif font-semibold text-card-foreground leading-snug group-hover:text-primary transition-colors">
                      {guide.title}
                    </CardTitle>

                    <CardDescription className="text-sm text-muted-foreground font-sans leading-relaxed pt-1">
                      {guide.summary}
                    </CardDescription>
                  </CardHeader>
                </div>

                {/* Card Action & Footer */}
                <CardFooter className="p-6 pt-3 border-t border-border/60 flex items-center justify-between mt-auto">
                  <div className="flex items-center gap-2 text-xs font-mono text-muted-foreground">
                    <BookOpen className="size-3.5 text-muted-foreground" />
                    <span>Free PDF</span>
                  </div>

                  <Button asChild size="sm" variant="default" className="rounded-sm px-4 py-2 text-xs font-medium">
                    <Link to={guide.downloadUrl}>
                      <span>Read overview</span>
                      <ArrowRight className="size-3.5 transition-transform duration-200 group-hover:translate-x-0.5" />
                    </Link>
                  </Button>
                </CardFooter>
              </Card>
            </motion.div>)}
        </motion.div>

        {/* Supplementary Library Index Strip */}
        <div className="mt-14 sm:mt-18 pt-8 border-t border-border flex flex-col sm:flex-row items-center justify-between gap-4 text-center sm:text-left">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-sm bg-muted text-foreground">
              <Layers className="size-4" />
            </div>
            <div>
              <h3 className="text-sm font-semibold font-serif text-foreground">
                Looking for quantitative industry datasets?
              </h3>
              <p className="text-xs text-muted-foreground font-sans">
                Access deep market intelligence reports and peer-reviewed surveys.
              </p>
            </div>
          </div>

          <Button asChild variant="outline" size="sm" className="rounded-sm shrink-0 text-xs font-medium">
            <Link to="/ResearchReportsPage">
              <span>View research reports</span>
              <ArrowRight className="size-3.5" />
            </Link>
          </Button>
        </div>
      </div>
    </section>;
}